import { useCallback, useEffect, useRef, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext } from './AuthContext';
import { AuthRecoveryCoordinator, classifyRefreshOutcome, getRetainedAuthState, normalizeAuthReturnPath, resolveRefreshAuthState } from './authSessionResilience';
import type { AuthRecoveryState } from './AuthContext';
import {
  clearPendingAnonymousSessionTransfer,
  createPendingAnonymousSessionTransferForAuthChange,
  persistPendingAnonymousSessionTransfer,
  readPendingAnonymousSessionTransfer,
  type PendingAnonymousSessionTransfer,
} from './anonymousSessionTransfer';
import { getSupabaseFunctionErrorMessage } from '@/lib/supabase-function-errors';

type UserWithAnonymousFlag = User & { is_anonymous?: boolean };

const isAnonymousUser = (authUser: User | null | undefined) =>
  (authUser as UserWithAnonymousFlag | null | undefined)?.is_anonymous === true;

const getUserMetadata = (authUser: User | null | undefined): Record<string, unknown> =>
  (authUser?.user_metadata ?? {}) as Record<string, unknown>;

const getProfileDefaults = (authUser: User) => {
  const metadata = getUserMetadata(authUser);
  const metadataName = typeof metadata.name === 'string' ? metadata.name.trim() : '';
  const metadataFullName = typeof metadata.full_name === 'string' ? metadata.full_name.trim() : '';
  const email = authUser.email?.trim() || null;
  const isAnonymous = isAnonymousUser(authUser);

  return {
    email,
    name:
      metadataName ||
      metadataFullName ||
      (email ? email.split('@')[0] : 'Guest'),
    isAuthor: metadata.is_author === true,
    isAnonymous,
  };
};

const ensureProfileAndCreditDefaults = async (authUser: User) => {
  const defaults = getProfileDefaults(authUser);

  try {
    const { data: existingProfile, error: profileReadError } = await supabase
      .from('profiles')
      .select('user_id')
      .eq('user_id', authUser.id)
      .maybeSingle();

    if (profileReadError) {
      console.warn('[Auth] Profile lookup failed:', profileReadError.message);
    } else if (!existingProfile) {
      const { error: profileInsertError } = await supabase
        .from('profiles')
        .insert({
          user_id: authUser.id,
          email: defaults.email,
          name: defaults.name,
          is_author: defaults.isAuthor,
          is_anonymous: defaults.isAnonymous,
        });

      if (profileInsertError && profileInsertError.code !== '23505') {
        console.warn('[Auth] Profile creation failed:', profileInsertError.message);
      }
    }

    const { data: existingAttrs, error: attrsReadError } = await supabase
      .from('profile_attributes')
      .select('attribute_name')
      .eq('user_id', authUser.id);

    if (attrsReadError) {
      console.warn('[Auth] Credit defaults lookup failed:', attrsReadError.message);
      return;
    }

    const existingNames = new Set((existingAttrs ?? []).map((attr) => attr.attribute_name));
    const missingAttrs = [
      { user_id: authUser.id, attribute_name: 'plan', attribute_value: 'free' },
      { user_id: authUser.id, attribute_name: 'credits', attribute_value: '50' },
      { user_id: authUser.id, attribute_name: 'plan_start', attribute_value: new Date().toISOString() },
    ].filter((attr) => !existingNames.has(attr.attribute_name));

    if (missingAttrs.length) {
      const { error: attrsInsertError } = await supabase
        .from('profile_attributes')
        .insert(missingAttrs);

      if (attrsInsertError && attrsInsertError.code !== '23505') {
        console.warn('[Auth] Credit defaults creation failed:', attrsInsertError.message);
      }
    }
  } catch (err) {
    console.warn('[Auth] Failed to ensure profile defaults:', err);
  }
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthor, setIsAuthor] = useState(false);
  const [isAuthorLoading, setIsAuthorLoading] = useState(true);
  const [anonymousTransfer, setAnonymousTransfer] = useState<PendingAnonymousSessionTransfer | null>(
    () => readPendingAnonymousSessionTransfer(),
  );
  const [anonymousTransferError, setAnonymousTransferError] = useState<string | null>(null);
  const [authRecoveryState, setAuthRecoveryState] = useState<AuthRecoveryState>('ready');
  const lastKnownSessionRef = useRef<Session | null>(null);
  const lastKnownUserRef = useRef<User | null>(null);
  const anonymousTransferRef = useRef<PendingAnonymousSessionTransfer | null>(anonymousTransfer);
  const anonymousTransferInFlightRef = useRef(false);
  const recoveryCoordinatorRef = useRef<AuthRecoveryCoordinator | null>(null);

  // Derive isAnonymous from user JWT metadata. Supabase sets `is_anonymous: true`
  // on users created via signInAnonymously().
  const isAnonymous = isAnonymousUser(user);

  const applyAuthState = (nextSession: Session | null, nextUser?: User | null) => {
    const resolvedUser = nextUser ?? nextSession?.user ?? null;
    lastKnownSessionRef.current = nextSession;
    lastKnownUserRef.current = resolvedUser;
    setSession(nextSession);
    setUser(resolvedUser);
  };

  const rememberAnonymousTransfer = (previousSession: Session | null, nextSession: Session | null) => {
    const pending = createPendingAnonymousSessionTransferForAuthChange(previousSession, nextSession);
    if (!pending) return null;
    persistPendingAnonymousSessionTransfer(pending);
    anonymousTransferRef.current = pending;
    setAnonymousTransfer(pending);
    setAnonymousTransferError(null);
    return pending;
  };

  const completeAnonymousTransfer = useCallback(async (): Promise<boolean> => {
    const pending = anonymousTransferRef.current;
    const targetSession = lastKnownSessionRef.current;
    if (!pending || !targetSession?.access_token || !targetSession.user?.id || isAnonymousUser(targetSession.user)) {
      return false;
    }
    if (anonymousTransferInFlightRef.current) return false;

    if (pending.sourceUserId === targetSession.user.id) {
      clearPendingAnonymousSessionTransfer();
      anonymousTransferRef.current = null;
      setAnonymousTransfer(null);
      setAnonymousTransferError(null);
      setLoading(false);
      return true;
    }

    anonymousTransferInFlightRef.current = true;
    setAnonymousTransferError(null);
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<{ success?: boolean }>(
        'transfer-anonymous-session',
        {
          body: { anonymousAccessToken: pending.anonymousAccessToken },
          headers: { Authorization: `Bearer ${targetSession.access_token}` },
        },
      );
      if (error || data?.success !== true) {
        const detail = error
          ? await getSupabaseFunctionErrorMessage(error, 'Unable to restore guest work.')
          : 'The transfer did not complete.';
        throw new Error(detail);
      }

      clearPendingAnonymousSessionTransfer();
      anonymousTransferRef.current = null;
      setAnonymousTransfer(null);
      setAnonymousTransferError(null);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('[Auth] Anonymous session transfer failed:', error);
      setAnonymousTransferError('We signed you in, but could not restore your guest work. Please try again.');
      setLoading(false);
      return false;
    } finally {
      anonymousTransferInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    anonymousTransferRef.current = anonymousTransfer;
    if (
      anonymousTransfer
      && session?.access_token
      && session.user?.id
      && !isAnonymousUser(session.user)
    ) {
      void completeAnonymousTransfer();
    }
  }, [anonymousTransfer, completeAnonymousTransfer, session?.access_token, session?.user]);

  useEffect(() => {
    const isAuthCallback = () =>
      typeof window !== 'undefined' && (
        (window.location.hash && window.location.hash.includes('access_token')) ||
        (window.location.search && window.location.search.includes('code='))
      );

    setLoading(true);

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const pendingTransfer = rememberAnonymousTransfer(lastKnownSessionRef.current, session);
      applyAuthState(session);

      // Immediately derive author flag from session metadata (prevents redirect race)
      const metaAuthor = getUserMetadata(session?.user).is_author === true;
      if (session?.user) {
        void ensureProfileAndCreditDefaults(session.user);
        setIsAuthor(metaAuthor);
        // Anonymous users are never authors — skip the profiles lookup.
        if (isAnonymousUser(session.user)) {
          setIsAuthor(false);
          setIsAuthorLoading(false);
        } else {
          setIsAuthorLoading(true);
          // Fetch authoritative value from profiles table (updates when ready)
          setTimeout(() => {
            supabase
              .from('profiles')
              .select('is_author')
              .eq('user_id', session.user.id)
              .maybeSingle()
              .then(({ data }) => {
                if (typeof data?.is_author === 'boolean') {
                  setIsAuthor(data.is_author);
                }
                setIsAuthorLoading(false);
              });
          }, 0);
        }
      } else {
        setIsAuthor(false);
        setIsAuthorLoading(false);
      }

      const inCallback = isAuthCallback();
      if (event === 'INITIAL_SESSION' && inCallback && !session) {
        // During OAuth callback, wait for SIGNED_IN after token exchange
        return;
      }
      // Any other auth event: we can end loading
      if (!pendingTransfer && !(anonymousTransferRef.current && session?.user && !isAnonymousUser(session.user))) {
        setLoading(false);
      }
    });

    // THEN check for existing session
    const initializeSession = async () => {
      let existingSession: Session | null = null;
      let existingUser: User | null = null;

      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          throw error;
        }
        existingSession = data.session;
      } catch (err) {
        const retainedState = getRetainedAuthState({
          session: lastKnownSessionRef.current,
          user: lastKnownUserRef.current,
        });
        existingSession = retainedState.session;
        existingUser = retainedState.user;
        console.warn('[Auth] Session initialization failed; preserving local session state:', err);
      }

      applyAuthState(existingSession, existingUser);

      if (existingSession) {
        void ensureProfileAndCreditDefaults(existingSession.user);
        if (!(anonymousTransferRef.current && !isAnonymousUser(existingSession.user))) {
          setLoading(false);
        }
        return;
      }

      if (isAuthCallback()) {
        // OAuth callback in progress — wait for onAuthStateChange to fire.
        return;
      }

      // No valid session and not in OAuth callback: remain signed out.
      setLoading(false);
    };

    void initializeSession();

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      console.error('Error signing out:', error);
    }
  };

  const refreshSession = async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      const nextState = resolveRefreshAuthState({
        session: lastKnownSessionRef.current,
        user: lastKnownUserRef.current,
        refreshedSession: data.session,
        error: error ?? null,
      });
      if (nextState.preserved) {
        console.warn('[Auth] Failed to refresh session; preserving current session state:', error);
      }
      applyAuthState(nextState.session, nextState.user);
      return Boolean(data.session);
    } catch (error) {
      const retainedState = getRetainedAuthState({
        session: lastKnownSessionRef.current,
        user: lastKnownUserRef.current,
      });
      applyAuthState(retainedState.session, retainedState.user);
      console.error('[Auth] Error refreshing session; preserving current session state:', error);
      return false;
    }
  };

  const routeToSignIn = useCallback(() => {
    if (typeof window === 'undefined') return;
    const returnTo = normalizeAuthReturnPath(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    window.location.assign(`/auth?returnTo=${encodeURIComponent(returnTo)}`);
  }, []);

  // The coordinator intentionally calls the latest refresh implementation via
  // a ref-like closure so all App editor/ticket requests share one flight.
  if (!recoveryCoordinatorRef.current) {
    recoveryCoordinatorRef.current = new AuthRecoveryCoordinator(
      async () => {
        const { data, error } = await supabase.auth.refreshSession();
        const nextState = resolveRefreshAuthState({
          session: lastKnownSessionRef.current,
          user: lastKnownUserRef.current,
          refreshedSession: data.session,
          error: error ?? null,
        });
        applyAuthState(nextState.session, nextState.user);
        return classifyRefreshOutcome(data.session, error);
      },
      () => { void supabase.auth.signOut({ scope: 'local' }); routeToSignIn(); },
      setAuthRecoveryState,
    );
  }

  const recoverSession = useCallback(() => recoveryCoordinatorRef.current!.recover(), []);
  const runAuthenticatedRequest = useCallback(<T,>(request: () => Promise<T>) =>
    recoveryCoordinatorRef.current!.run(request), []);

  const value = {
    user,
    session,
    loading,
    isAuthor,
    isAuthorLoading,
    isAnonymous,
    anonymousTransferPending: Boolean(anonymousTransfer),
    anonymousTransferError,
    retryAnonymousTransfer: completeAnonymousTransfer,
    signOut,
    refreshSession,
    authRecoveryState,
    recoverSession,
    runAuthenticatedRequest,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
